import { Button, colors, DateRangePicker, Option, SelectInput, TextInput } from '@prenuvo/shared-library';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useAuth, useFilter, useStudies, useTable } from '../hooks';
import { DateRangeType, Location } from '../types';

export const PAGE_LIMIT = 25;

const Header = styled.div`
  background-color: ${colors.pearl};
`;

const Wrapper = styled.div`
  margin: 0 auto;
  max-width: 1440px;
  display: flex;
  justify-content: space-between;
  position: relative;
  padding: 24px 5px;

  gap: 30px 20px;
`;

export const ButtonWrapper = styled.div`
  margin-top: 1.75%;
  text-align: center;
`;

const StatusSelect = styled(SelectInput)`
  padding-right: 20px;
  border-right: 2px solid ${colors.smoke};
`;

const Filters = () => {
  const { tab } = useParams<{ tab: string }>();
  const {
    dateRange,
    setDateRange,
    location,
    setLocation,
    status,
    setStatus,
    searchTerm,
    setSearchTerm,
    selectedNP,
    setSelectedNP,
    getFormattedFilters,
  } = useFilter();

  const { closeTabs, getIndicators } = useTable();
  const { allowedLocations, allowedPrescriberLocations, view, npOptions } = useAuth();
  const { fetchStudies, setStudies, setFormattedStudies } = useStudies();

  const handleDateRangeChange = (dateRange: DateRangeType) => setDateRange(dateRange);

  const locationsToCreateOptions =
    tab === 'prescriptions' && view === 'np' ? allowedPrescriberLocations : allowedLocations;

  const locationOptions =
    (locationsToCreateOptions &&
      locationsToCreateOptions
        .map((location: Location) => ({
          value: location.code,
          label: location.name,
        }))
        .filter(({ label }: { label: string }) => label)) ||
    [];

  locationOptions.splice(0, 0, { value: 'all', label: 'All' });

  const handleLocationChange = (val: Option) => {
    setLocation(val);
  };

  const statusOptions =
    tab === 'referrals'
      ? [
          { value: 'requiring-referrals', label: 'Pending' },
          { value: 'referred', label: 'Completed' },
          { value: 'not-referred', label: 'Denied' },
        ]
      : tab === 'consultations'
      ? [
          { value: 'requiring-consult', label: 'Upcoming Consultation' },
          { value: 'already-consulted', label: 'Consultation Complete' },
          { value: 'not-consulted', label: 'Consultation Not Complete' },
        ]
      : tab === 'prescriptions'
      ? [
          { value: 'prescription-pending', label: 'Pending' },
          { value: 'prescription-ready-for-pharmacy', label: 'Ready For Pharmacy' },
          { value: 'prescription-sent', label: 'Sent to Pharmacy' },
          { value: 'prescription-ready', label: 'Ready For Pickup' },
          { value: 'prescription-cancelled', label: 'Cancelled' }
        ]
      : [];

  const applyFilter = async () => {
    const formattedFilter = await getFormattedFilters();

    if (formattedFilter?.skipFilter === true || (!formattedFilter.users && searchTerm !== '')) {
      setStudies([]);
    } else {
      if (formattedFilter.status) {
        const { data: studyData } = await fetchStudies({
          context: {
            clientName: 'apps',
          },
          variables: {
            studyFilter: formattedFilter,
          },
        });
        if (studyData?.npdStudies?.edges?.length) {
          const studyResponse = studyData.npdStudies.edges.map(({ node }: { node: any }) => node);

          setFormattedStudies(studyResponse);
          if (status.includes('prescription-')){
            getIndicators();
          }
        } else {
          setStudies([]);
        }
      }
    }
  };

  const applyFilterAndCloseTabs = () => {
    applyFilter();
    closeTabs();
  };

  useEffect(() => {
    applyFilter();
  }, [status]);

  return (
    <>
      <Header>
        <Wrapper>
          <StatusSelect
            options={statusOptions}
            variant="primary"
            placeholder=""
            label="Status"
            value={statusOptions.find((option: Option) => option.value === status)}
            onChange={(val: Option) => {
              closeTabs();
              setStatus(val.value);
            }}
          />
          <TextInput
            value={searchTerm}
            id={'searchTerm'}
            key={'searchTerm'}
            label={'Search Name'}
            placeholder={'Search'}
            icon
            onChange={(input: string) => setSearchTerm(input)}
          />
          {view === 'np_coordinator' && (
            <SelectInput
              options={
                tab === 'referrals' || tab === 'prescriptions'
                  ? [
                      { value: 'None', label: 'Not Assigned' },
                      { value: 'all', label: 'All' },
                      ...npOptions,
                    ]
                  : [{ value: 'all', label: 'All' }, ...npOptions]
              }
              variant="primary"
              placeholder=""
              label="Nurse Practitioner"
              value={selectedNP}
              onChange={(val: Option) => {
                setSelectedNP(val);
              }}
            />
          )}
          <SelectInput
            options={locationOptions}
            variant="primary"
            placeholder=""
            label="Location"
            value={location}
            onChange={(val: Option) => {
              handleLocationChange(val);
            }}
          />
          <DateRangePicker
            onChange={handleDateRangeChange as any}
            value={dateRange}
            showPastDays
            label={'Dates'}
          />
          <ButtonWrapper>
            <Button variant={'primary'} size={'small'} onClick={applyFilterAndCloseTabs}>
              Search
            </Button>
          </ButtonWrapper>
        </Wrapper>
      </Header>
    </>
  );
};

export default Filters;
